import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarCheck, Check, ChevronDown, ChevronLeft, ChevronRight, Columns3, CalendarDays, Globe, Link2, PanelLeft, Plus, SlidersHorizontal, Square } from 'lucide-react';
import { rangeLabel, zoneOffsetLabel } from '../../lib/calendar/zonedCalendar';

export const VIEW_OPTIONS = [
    { key: 'day', label: 'Day', icon: Square, shortcut: 'D' },
    { key: 'week', label: 'Week', icon: Columns3, shortcut: 'W' },
    { key: 'month', label: 'Month', icon: CalendarDays, shortcut: 'M' },
];

/** Open/close state for a toolbar menu: closes on an outside press or Escape. */
function useMenu() {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    useEffect(() => {
        if (!open) return undefined;
        const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
    }, [open]);
    return { open, setOpen, ref };
}

const menuPanel = 'absolute top-[39px] right-0 z-40 p-1.5 bg-white border border-[#E9E7E2] rounded-xl shadow-[0_12px_28px_rgba(12,10,9,.13)]';

const outlineBtn = 'inline-flex items-center gap-[7px] h-[34px] px-3.5 border border-[#E4E1DB] rounded-[9px] bg-white text-[13px] font-medium text-[#3A3532] hover:bg-[#F4F2EE]';
const iconBtn = 'w-[30px] h-[30px] rounded-lg flex items-center justify-center text-[#57504B] hover:bg-[#F4F2EE]';

/**
 * The calendar toolbar (#677, prototype `isCalGrid` bar): New event · Today ·
 * ‹ › · period label · zone · view menu.
 *
 * The zone statement (D6) is a SIBLING of the date controls and never inside
 * `calendar-view-controls`: the grid renders every label in one stated zone,
 * and putting the statement inside a stateful control would imply it changes
 * with it. The visible text is the prototype's short offset; the full
 * "Times shown in <zone>" statement is in the same element for screen readers
 * (and is what Calendar.displayZone.test asserts).
 *
 * "Calendar settings" (the prototype's sliders button, which it leaves
 * unwired) opens the two settings the calendar actually has: the display
 * time zone (Profile) and the connected calendars (Integrations).
 */
export function CalendarToolbar({
    view, onViewChange, anchorDay, displayZone, onPrev, onNext, onToday,
    canCreate, createDisabledReason, onNewEvent, onToggleRail, railOpen,
}) {
    const { open: menuOpen, setOpen: setMenuOpen, ref: menuRef } = useMenu();
    const settings = useMenu();
    const current = VIEW_OPTIONS.find(v => v.key === view) || VIEW_OPTIONS[1];

    const periodWord = view === 'month' ? 'month' : view === 'day' ? 'day' : 'week';

    return (
        <div className="flex flex-none flex-wrap items-center gap-2.5 px-[18px] py-2.5 border-b border-[#EDEBE6] bg-white" data-testid="calendar-toolbar">
            <button
                type="button"
                onClick={onToggleRail}
                aria-label={railOpen ? 'Hide calendar sidebar' : 'Show calendar sidebar'}
                aria-expanded={railOpen}
                className={`${iconBtn} lg:hidden`}
                data-testid="calendar-rail-toggle"
            >
                <PanelLeft className="w-4 h-4" />
            </button>
            <span title={canCreate ? undefined : createDisabledReason} className="inline-flex">
                <button
                    type="button"
                    onClick={onNewEvent}
                    disabled={!canCreate}
                    aria-describedby={canCreate ? undefined : 'calendar-create-disabled-reason'}
                    className="inline-flex items-center gap-[7px] h-[34px] px-3.5 rounded-[9px] bg-[#4D11F6] text-white text-[13px] font-semibold hover:bg-[#3B0DBE] disabled:opacity-50 disabled:hover:bg-[#4D11F6] disabled:cursor-not-allowed"
                    data-testid="new-meeting-btn"
                >
                    <Plus className="w-[15px] h-[15px]" /> New event
                </button>
            </span>
            {!canCreate && <span id="calendar-create-disabled-reason" className="sr-only">{createDisabledReason}</span>}
            <button type="button" onClick={onToday} className={outlineBtn} data-testid="nav-today">
                <CalendarCheck className="w-[15px] h-[15px] text-[#57504B]" /> Today
            </button>
            <div className="flex items-center gap-0.5">
                <button type="button" onClick={onPrev} aria-label={`Previous ${periodWord}`} title={`Previous ${periodWord}`} className={iconBtn} data-testid="nav-prev">
                    <ChevronLeft className="w-[17px] h-[17px]" />
                </button>
                <button type="button" onClick={onNext} aria-label={`Next ${periodWord}`} title={`Next ${periodWord}`} className={iconBtn} data-testid="nav-next">
                    <ChevronRight className="w-[17px] h-[17px]" />
                </button>
            </div>
            <h1 className="text-[15px] font-semibold tracking-[-0.01em] text-[#0C0A09] whitespace-nowrap" data-testid="calendar-range-label">
                {rangeLabel(view, anchorDay)}
            </h1>
            <span className="text-xs text-[#A29B95] whitespace-nowrap" title={`Times shown in ${displayZone}`} data-testid="calendar-display-zone">
                <span aria-hidden="true">{zoneOffsetLabel(displayZone)}</span>
                <span className="sr-only">Times shown in {displayZone}</span>
            </span>
            <div className="flex-1" />
            <div className="relative" ref={menuRef} data-testid="calendar-view-controls">
                <button
                    type="button"
                    onClick={() => setMenuOpen(o => !o)}
                    aria-haspopup="menu"
                    aria-expanded={menuOpen}
                    className="inline-flex items-center gap-2 h-[34px] pl-3 pr-[11px] border border-[#E4E1DB] rounded-[9px] bg-white text-[13px] font-medium text-[#3A3532] hover:bg-[#F4F2EE]"
                    data-testid="view-menu-trigger"
                >
                    <current.icon className="w-[15px] h-[15px] text-[#57504B]" />
                    <span>{current.label}</span>
                    <ChevronDown className="w-3.5 h-3.5 text-[#8A837E]" />
                </button>
                {menuOpen && (
                    <div role="menu" className={`${menuPanel} w-[186px]`}>
                        {VIEW_OPTIONS.map(v => (
                            <button
                                key={v.key}
                                type="button"
                                role="menuitemradio"
                                aria-checked={v.key === view}
                                onClick={() => { onViewChange(v.key); setMenuOpen(false); }}
                                className={`w-full flex items-center gap-[9px] h-[34px] px-[9px] rounded-lg text-[13px] text-left ${v.key === view ? 'bg-[#F4F2EE] text-[#0C0A09] font-semibold' : 'text-[#3A3532] hover:bg-[#F4F2EE]'}`}
                                data-testid={`view-${v.key}`}
                            >
                                <span className="w-4 flex justify-center">{v.key === view && <Check className="w-3.5 h-3.5 text-[#4D11F6]" />}</span>
                                <v.icon className="w-[15px] h-[15px] text-[#57504B]" />
                                <span className="flex-1">{v.label}</span>
                                <kbd className="text-[11px] font-sans text-[#A29B95]">{v.shortcut}</kbd>
                            </button>
                        ))}
                    </div>
                )}
            </div>
            <div className="relative" ref={settings.ref}>
                <button
                    type="button"
                    onClick={() => settings.setOpen(o => !o)}
                    aria-haspopup="menu"
                    aria-expanded={settings.open}
                    aria-label="Calendar settings"
                    title="Calendar settings"
                    className="w-[34px] h-[34px] rounded-[9px] flex items-center justify-center text-[#57504B] hover:bg-[#F4F2EE]"
                    data-testid="calendar-settings-trigger"
                >
                    <SlidersHorizontal className="w-4 h-4" />
                </button>
                {settings.open && (
                    <div role="menu" aria-label="Calendar settings" className={`${menuPanel} w-[260px]`}>
                        <Link
                            to="/settings/profile"
                            role="menuitem"
                            onClick={() => settings.setOpen(false)}
                            className="flex items-start gap-[9px] px-[9px] py-2 rounded-lg hover:bg-[#F4F2EE]"
                            data-testid="calendar-settings-timezone"
                        >
                            <Globe className="w-[15px] h-[15px] mt-0.5 flex-none text-[#57504B]" />
                            <span className="min-w-0">
                                <span className="block text-[13px] text-[#0C0A09]">Time zone</span>
                                <span className="block text-[11.5px] text-[#8A837E] truncate">{zoneOffsetLabel(displayZone)} · {displayZone}</span>
                                <span className="block text-[11.5px] text-[#A29B95]">Change it in your profile</span>
                            </span>
                        </Link>
                        <Link
                            to="/settings/integrations"
                            role="menuitem"
                            onClick={() => settings.setOpen(false)}
                            className="flex items-start gap-[9px] px-[9px] py-2 rounded-lg hover:bg-[#F4F2EE]"
                            data-testid="calendar-settings-integrations"
                        >
                            <Link2 className="w-[15px] h-[15px] mt-0.5 flex-none text-[#57504B]" />
                            <span className="min-w-0">
                                <span className="block text-[13px] text-[#0C0A09]">Connected calendars</span>
                                <span className="block text-[11.5px] text-[#8A837E]">Google Calendar and Outlook</span>
                            </span>
                        </Link>
                    </div>
                )}
            </div>
        </div>
    );
}
